import { useCallback, useEffect, useRef, useState } from "react";
import { getRecognition } from "../components/VoiceSearch/utils";

const SpeechRecognition = getRecognition();

export function useVoiceRecognition(defaultLang: string) {
  const recognitionRef = useRef<any>(null);
  const isListeningRef = useRef(false);

  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const targetScaleRef = useRef(0.7);

  const [isListening, setIsListening] = useState(false);
  const [language, setLanguage] = useState(defaultLang);
  const [error, setError] = useState<string | null>(null);

  const [interimText, setInterimText] = useState("");
  const [finalText, setFinalText] = useState("");

  const [rippleScale, setRippleScale] = useState(0.7);

  const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

  // Init recognition (runs once)
  useEffect(() => {
    if (!SpeechRecognition) {
      setError("Voice Recognition not supported in this browser");
      return;
    }

    const rec = new SpeechRecognition();
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.lang = language;

    rec.onstart = () => {
      isListeningRef.current = true;
      setIsListening(true);
      startAudioAnalyser();
      animateRipple();
    };

    rec.onend = () => {
      isListeningRef.current = false;
      setIsListening(false);
      stopAudioAnalyser();
      targetScaleRef.current = 0.7;
      setRippleScale(0.7);
    };

    rec.onerror = (event: any) => {
      setError(event.error);
      isListeningRef.current = false;
      setIsListening(false);
      stopAudioAnalyser();
    };

    rec.onresult = (event: any) => {
      let interim = "";
      let final = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += text;
        else interim += text;
      }

      if (interim) setInterimText(interim.trim());
      else setInterimText("");

      if (final) {
        setFinalText((prev) =>
          prev ? prev + " " + final.trim() : final.trim()
        );
        setInterimText("");
      }
    };

    recognitionRef.current = rec;
    // start();

    return () => {
      try {
        rec.stop();
      } catch {}
    };
  }, []);

  // AUDIO --------------------------------------------------

  const startAudioAnalyser = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioCtx = new (window.AudioContext ||
        (window as any).webkitAudioContext)();
      const source = audioCtx.createMediaStreamSource(stream);

      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      source.connect(analyser);
      analyserRef.current = analyser;
      dataArrayRef.current = dataArray;

      updateVolume();
    } catch (err) {
      console.error(err);
    }
  };

  const stopAudioAnalyser = () => {
    analyserRef.current = null;
    dataArrayRef.current = null;
  };

  const updateVolume = () => {
    if (
      !analyserRef.current ||
      !dataArrayRef.current ||
      !isListeningRef.current
    )
      return;

    analyserRef.current.getByteTimeDomainData(dataArrayRef.current as any);

    let sum = 0;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      const val = dataArrayRef.current[i] - 128;
      sum += val * val;
    }

    const rms = Math.sqrt(sum / dataArrayRef.current.length);
    targetScaleRef.current = 0.7 + Math.min(rms / 30, 0.9);

    requestAnimationFrame(updateVolume);
  };

  const animateRipple = () => {
    const step = () => {
      setRippleScale((prev) => lerp(prev, targetScaleRef.current, 0.1));
      if (isListeningRef.current) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  // CONTROL --------------------------------------------------

  const start = useCallback(() => {
    setError(null);
    try {
      // document.body.click();
      recognitionRef.current?.start();
    } catch {}
  }, []);

  const stop = useCallback(() => {
    setError(null);
    try {
      recognitionRef.current?.stop();
    } catch {}
  }, []);

  const restart = useCallback(() => {
    setError(null);
    try {
      recognitionRef.current?.stop();
    } catch {}

    setTimeout(() => {
      try {
        start();
      } catch {}
    }, 250);
  }, []);

  const changeLanguage = useCallback(
    (lang: string) => {
      setLanguage(lang);
      if (recognitionRef.current) {
        recognitionRef.current.lang = lang;
        restart();
      }
    },
    [restart]
  );

  const resetText = useCallback(() => {
    setInterimText("");
    setFinalText("");
  }, []);

  // RETURN --------------------------------------------------

  return {
    isListening,
    isListeningRef,
    language,
    changeLanguage,
    error,

    interimText,
    finalText,
    resetText,

    rippleScale,

    start,
    stop,
    restart,
  };
}
